/**
 * CuttySark 賞のステージ画面（Meet で画面共有する演出画面）で使う純粋関数。
 *
 * 結果発表は、運営が順位単位でめくった状態（reveal）から「いま発表している部門と順位」を求め、
 * その人たちだけを溜め → 発表の流れで大きく出す。順位や勝ち上がりの判定はしない
 * （API が付けた値をそのまま使う）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面の演出補助（いま発表している順位・表彰台・コメントの流し方・紙吹雪の配置）
 * @graph-connects none
 */

import type {
  CuttysarkDivisionResult,
  CuttysarkLiveCandidate,
  CuttysarkResultEntry,
  CuttysarkReveal,
} from '@cortex/domain-product-innovation-lab-types';

/**
 * 結果の 1 行を識別するキー（部門 + 候補者）。
 * @graph-connects none
 */
export function resultKey(divisionId: string, candidateEmail: string): string {
  return `${divisionId}:${candidateEmail}`;
}

/** ステージ画面でいま発表している順位 @graph-connects none */
export interface RevealFocus {
  /** 発表のやり直しを見分けるキー（部門 + 順位）。 */
  key: string;
  divisionId: string;
  rank: number;
  /** その順位の人（同順位なら複数）。 */
  entries: CuttysarkResultEntry[];
}

/**
 * いま発表している順位。最後にめくった部門（reveal の末尾）の、めくった中でいちばん上の順位の人たち。
 * 運営には全結果が返るので、見えている結果ではなくめくった人数（count）から求める
 * （ステージ画面は運営の画面を Meet で共有するため、全結果から出すと一度に全部出てしまう）。
 * まだ何もめくっていなければ null。
 * @graph-connects none
 */
export function revealFocus(
  results: readonly CuttysarkDivisionResult[],
  reveal: readonly CuttysarkReveal[],
): RevealFocus | null {
  const last = reveal[reveal.length - 1];
  if (last === undefined || last.count <= 0) return null;
  const result = results.find((r) => r.divisionId === last.divisionId);
  if (result === undefined) return null;
  // 参加者には count 人ぶんだけ返ってくるので、そのときは先頭から全部がめくった人になる
  const shown = result.entries.slice(Math.max(result.entries.length - last.count, 0));
  const top = shown[0];
  if (top === undefined) return null;
  return {
    key: `${last.divisionId}:${String(top.rank)}`,
    divisionId: last.divisionId,
    rank: top.rank,
    entries: shown.filter((e) => e.rank === top.rank),
  };
}

/** 表彰台に乗せる 1 人 @graph-connects none */
export interface PodiumEntry {
  entry: CuttysarkResultEntry;
  candidate: CuttysarkLiveCandidate | undefined;
}

/**
 * 部門ごとの勝ち上がり（表彰台）。勝ち上がりの人を順位の高い順に並べる。
 * 枠の境目で同票の人も勝ち上がり扱いで返ってくるので、そのまま並べる（判断は運営）。
 * @graph-connects none
 */
export function podiumOf(
  result: CuttysarkDivisionResult,
  candidates: readonly CuttysarkLiveCandidate[],
): PodiumEntry[] {
  return result.entries
    .filter((e) => e.advancing)
    .sort((a, b) => a.rank - b.rank)
    .map((entry) => ({
      entry,
      candidate: candidates.find((c) => c.email === entry.candidateEmail),
    }));
}

/** 画面を流れる応援コメント @graph-connects none */
export interface FlowingComment {
  key: string;
  text: string;
  /** 宛先の候補者の表示名（投票者は伏せたまま）。 */
  to: string;
  /** 何段目を流すか（0 始まり）。 */
  lane: number;
  /** 流し始めるまでの遅れ（ミリ秒）。 */
  delayMs: number;
}

/** コメントを流す段数 @graph-connects none */
export const COMMENT_LANES = 6;
/** 同じ段で次のコメントを流すまでの間隔（ミリ秒） @graph-connects none */
const COMMENT_INTERVAL_MS = 2500;

/**
 * 見えている行のコメントを、段と遅れを決めて流す順に並べる。
 * 段は順番に回し、同じ段の次のコメントは間隔を空ける（重ならないように）。
 * 並びは決定論的にする（ポーリングのたびに位置が変わって飛ばないように）。
 * @graph-connects resultKey [calls] コメントのキー
 */
export function flowingComments(
  results: readonly CuttysarkDivisionResult[],
  candidates: readonly CuttysarkLiveCandidate[],
  lanes: number = COMMENT_LANES,
): FlowingComment[] {
  const nameOf = new Map(candidates.map((c) => [c.email, c.name]));
  const all = results.flatMap((r) =>
    r.entries.flatMap((e) =>
      e.comments.map((text, i) => ({
        key: `${resultKey(r.divisionId, e.candidateEmail)}:${String(i)}`,
        text,
        to: nameOf.get(e.candidateEmail) ?? e.candidateEmail,
      })),
    ),
  );
  return all.map((c, index) => ({
    ...c,
    lane: index % lanes,
    delayMs: Math.floor(index / lanes) * COMMENT_INTERVAL_MS + (index % lanes) * 400,
  }));
}

/** 紙吹雪の 1 片 @graph-connects none */
export interface ConfettiPiece {
  /** 左端からの位置（%）。 */
  left: number;
  /** 落ち始めるまでの遅れ（ミリ秒）。 */
  delayMs: number;
  /** 落ちる時間（ミリ秒）。 */
  durationMs: number;
  /** 色の番号（0〜4）。 */
  color: number;
  /** 回転（度）。 */
  rotate: number;
}

/**
 * 紙吹雪の配置を作る。乱数ではなく番号から決める（再描画で位置が飛ばないように）。
 * @graph-connects none
 */
export function confettiPieces(count: number): ConfettiPiece[] {
  return Array.from({ length: count }, (_, i) => ({
    left: (i * 37) % 100,
    delayMs: (i * 53) % 900,
    durationMs: 2200 + ((i * 97) % 1600),
    color: i % 5,
    rotate: (i * 47) % 360,
  }));
}
